/**
 * Deterministic Mermaid label sanitizer.
 *
 * LLMs frequently emit flowchart node labels that contain Mermaid-special
 * characters (`(`, `)`, `/`, `<br>`, `&`, …) without wrapping them in double
 * quotes, e.g.
 *
 *   B[Chain of Custody Logging<br>(Asset Tagging & Tracking)]
 *
 * The unquoted `(` makes Mermaid's flowchart grammar throw
 * `Parse error ... got 'PS'`. Wrapping the label in double quotes makes Mermaid
 * treat the contents as literal text and the diagram renders fine:
 *
 *   B["Chain of Custody Logging<br>(Asset Tagging & Tracking)"]
 *
 * This module rewrites unquoted node/edge labels into quoted ones BEFORE the
 * code is handed to mermaid, so output from any model renders. It is:
 *   - flowchart-only (sequence/gantt/pie/etc. are returned untouched),
 *   - conservative (ambiguous tokens are left unchanged rather than corrupted),
 *   - idempotent (already-quoted labels are left as-is).
 */

/** A node-shape delimiter pair. Longest/compound pairs MUST come first so that
 * e.g. `[(` is matched before `[`, and `([` before `(`. */
interface ShapePair {
  open: string;
  close: string;
}

// Order matters: compound/longest delimiters before singles.
const SHAPE_PAIRS: ShapePair[] = [
  { open: '[[', close: ']]' }, // subroutine
  { open: '([', close: '])' }, // stadium
  { open: '[(', close: ')]' }, // cylinder / database
  { open: '((', close: '))' }, // circle
  { open: '{{', close: '}}' }, // hexagon
  { open: '[', close: ']' }, // rectangle
  { open: '(', close: ')' }, // rounded
  { open: '{', close: '}' }, // rhombus / decision
];

const NODE_ID_CHAR = /[A-Za-z0-9_]/;

/** Escape a string for safe use as a quoted Mermaid label. Mermaid does not
 * support backslash escapes inside quotes; the canonical way to embed a literal
 * double-quote is the HTML entity `#quot;`. */
function escapeLabel(content: string): string {
  return content.replace(/"/g, '#quot;');
}

/** Whether `content` (already trimmed) is already a fully double-quoted label. */
function isQuoted(content: string): boolean {
  return (
    content.length >= 2 && content.startsWith('"') && content.endsWith('"')
  );
}

/** True if the first meaningful line declares a flowchart (`graph`/`flowchart`).
 * Directives (`%%{...}%%`), front-matter-ish blank lines and `%%` comments are
 * skipped. Only flowcharts are sanitized. */
function isFlowchart(code: string): boolean {
  const lines = code.split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (line === '') continue;
    if (line.startsWith('%%')) continue;
    return /^(graph|flowchart)\b/i.test(line);
  }
  return false;
}

/**
 * Build the quoted form of `<nodeId><open><content><close>`, or `null` to leave
 * the original token untouched (empty or already-quoted labels).
 */
function quoteNodeLabel(
  nodeId: string,
  pair: ShapePair,
  content: string
): string | null {
  const trimmed = content.trim();
  if (trimmed === '') return null; // empty label — leave alone
  if (isQuoted(trimmed)) return null; // already quoted — idempotent
  // Ambiguity guard: if the label text itself contains this shape's opener or
  // closer delimiter, the boundaries are ambiguous (e.g. `B[Array[0]]`, or a
  // rounded node whose text has parentheses we'd close on too early). Closing on
  // the first delimiter would corrupt it, so leave the token unchanged — being
  // conservative is strictly safer than producing a broken diagram.
  if (trimmed.includes(pair.open) || trimmed.includes(pair.close)) return null;
  return `${nodeId}${pair.open}"${escapeLabel(trimmed)}"${pair.close}`;
}

/**
 * Quote node-shape labels in a single left-to-right scan.
 *
 * At each `<nodeId>` immediately followed by a shape opener (no whitespace
 * between id and opener), we consume content up to the FIRST matching closer and
 * quote it, then resume scanning AFTER that closer. Scanning past the closer is
 * what keeps single-delimiter shapes from re-entering the content of a compound
 * shape (so `E[(Database / store)]` is treated as one cylinder, never re-split
 * on the inner `[`/`(`). The longest opener at a position wins, so compound
 * shapes are preferred over their single-char prefixes.
 *
 * Requiring the nodeId to abut the opener means prose such as `Custody (Asset)`
 * — where a space precedes `(` — is never mistaken for a rounded node.
 */
function quoteNodeShapes(code: string): string {
  let out = '';
  let i = 0;
  const n = code.length;

  while (i < n) {
    // Find the start of a candidate nodeId: a run of id chars whose preceding
    // char is not an id char (token boundary), immediately followed by an opener.
    const ch = code[i];
    const prev = i > 0 ? code[i - 1] : '';
    const atBoundary = i === 0 || !NODE_ID_CHAR.test(prev);

    if (!(atBoundary && NODE_ID_CHAR.test(ch))) {
      out += ch;
      i++;
      continue;
    }

    // Consume the nodeId.
    let j = i;
    while (j < n && NODE_ID_CHAR.test(code[j])) j++;
    const nodeId = code.slice(i, j);

    // Pick the longest opener that starts at j (SHAPE_PAIRS is longest-first).
    const pair = SHAPE_PAIRS.find(p => code.startsWith(p.open, j));
    if (!pair) {
      // Not a shape — emit the nodeId verbatim and continue right after it.
      out += nodeId;
      i = j;
      continue;
    }

    const contentStart = j + pair.open.length;
    const closeIdx = code.indexOf(pair.close, contentStart);
    if (closeIdx === -1) {
      // Unterminated shape — ambiguous; leave untouched and continue past id.
      out += nodeId;
      i = j;
      continue;
    }

    const content = code.slice(contentStart, closeIdx);
    const replacement = quoteNodeLabel(nodeId, pair, content);
    if (replacement === null) {
      // Empty or already-quoted: emit the original token unchanged, then resume
      // scanning AFTER the closer so we never re-enter this shape's content.
      out += code.slice(i, closeIdx + pair.close.length);
    } else {
      out += replacement;
    }
    i = closeIdx + pair.close.length;
  }

  return out;
}

/**
 * Quote edge labels of the form `|<content>|`, e.g. `-->|Yes (maybe)|`.
 * Already-quoted edge labels (`|"Yes"|`) are left untouched.
 */
function quoteEdgeLabels(code: string): string {
  // Content must not contain a pipe so each label is scoped to its own pair.
  const re = /\|([^|]*)\|/g;
  return code.replace(re, (match, content) => {
    const trimmed = content.trim();
    if (trimmed === '') return match;
    if (isQuoted(trimmed)) return match;
    return `|"${escapeLabel(trimmed)}"|`;
  });
}

/**
 * Deterministically quote flowchart node and edge labels so that diagrams whose
 * labels contain Mermaid-special characters still render.
 *
 * Pure, idempotent, and flowchart-only. Non-flowchart diagrams and ambiguous
 * tokens are returned unchanged.
 */
export function quoteMermaidLabels(code: string): string {
  if (!code) return code;
  if (!isFlowchart(code)) return code;

  let result = quoteNodeShapes(code);
  result = quoteEdgeLabels(result);
  return result;
}
